import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, apiErr } from "@/lib/api";
import {
  UploadCloud, Loader2, Film, Video, AlertTriangle, X, Check, ChevronLeft,
  ChevronRight, Play, Info,
} from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, required, ...props }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-[0.12em] text-stone-500">
      {label} {required && <span className="text-emerald-600">*</span>}
    </label>
    <input {...props}
      className="mt-1.5 flex h-11 w-full rounded-md border border-stone-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
  </div>
);

const Stepper = ({ step }) => {
  const steps = ["Doctor details", "Choose template", "Add video"];
  return (
    <div className="flex items-center gap-2" data-testid="wizard-stepper">
      {steps.map((s, i) => {
        const n = i + 1;
        const active = step === n, done = step > n;
        return (
          <div key={s} className="flex items-center gap-2">
            <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${done ? "bg-emerald-600 text-white" : active ? "bg-emerald-100 text-emerald-800 ring-2 ring-emerald-500" : "bg-stone-100 text-stone-400"}`}>
              {done ? <Check className="h-4 w-4" /> : n}
            </div>
            <span className={`text-sm font-medium hidden sm:block ${active || done ? "text-stone-800" : "text-stone-400"}`}>{s}</span>
            {i < steps.length - 1 && <ChevronRight className="h-4 w-4 text-stone-300" />}
          </div>
        );
      })}
    </div>
  );
};

export default function UploadVideo() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const videoRefs = useRef({});
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [loadingTpl, setLoadingTpl] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [form, setForm] = useState({
    doctor_name: "", qualification: "", doctor_email: "", fb_insta: "",
    yt_channel: "", doctor_mobile: "", cc_number: "", place: "",
  });
  const [templateId, setTemplateId] = useState(null);
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    api.get("/templates").then(({ data }) => setTemplates(data)).catch(() => {}).finally(() => setLoadingTpl(false));
  }, []);

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const togglePlay = (id) => {
    // Pause any other template that's currently playing
    Object.entries(videoRefs.current).forEach(([vid, el]) => {
      if (vid !== id && el) { el.pause(); }
    });
    const el = videoRefs.current[id];
    if (!el) return;
    el.play().then(() => setPlayingId(id)).catch(() => {});
  };

  const pick = (f) => {
    if (!f) return;
    const ext = f.name.toLowerCase().slice(f.name.lastIndexOf("."));
    if (![".mp4", ".mov", ".webm"].includes(ext)) { toast.error("Video must be MP4, MOV or WEBM"); return; }
    if (f.size > 200 * 1024 * 1024) { toast.error("Video exceeds 200MB"); return; }
    setFile(f);
  };

  const next = () => {
    if (step === 1) {
      if (!form.doctor_name.trim() || !form.qualification.trim()) {
        toast.error("Doctor Name and Qualification are required"); return;
      }
    }
    if (step === 2 && !templateId) { toast.error("Please select a template"); return; }
    setStep((s) => Math.min(3, s + 1));
  };

  const submit = async () => {
    if (!file) { toast.error("Please upload or record a video"); return; }
    setUploading(true); setProgress(0);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("template_id", templateId);
    fd.append("source_kind", "upload");
    fd.append("file", file);
    try {
      await api.post("/videos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      toast.success("Submitted! Your branded video is being generated.");
      navigate("/mr/videos");
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setUploading(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="upload-video-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Create</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-stone-900 mt-1">New Greeting Video</h1>
      </div>

      <Stepper step={step} />

      {/* STEP 1 — doctor registration */}
      {step === 1 && (
        <div className="rounded-lg border border-stone-200 bg-white p-6 grid grid-cols-1 sm:grid-cols-2 gap-5 animate-fade-up" data-testid="step-doctor-details">
          <Field label="Name of Doctor" required data-testid="doctor-name-input" value={form.doctor_name} onChange={(e) => upd("doctor_name", e.target.value)} placeholder="Dr. Anita Rao" />
          <Field label="Qualification" required data-testid="doctor-qualification-input" value={form.qualification} onChange={(e) => upd("qualification", e.target.value)} placeholder="MD, Cardiology" />
          <Field label="Email" type="email" data-testid="doctor-email-input" value={form.doctor_email} onChange={(e) => upd("doctor_email", e.target.value)} placeholder="doctor@example.com" />
          <Field label="Mobile Number" data-testid="doctor-mobile-input" value={form.doctor_mobile} onChange={(e) => upd("doctor_mobile", e.target.value)} placeholder="98765 43210" />
          <Field label="FB / Insta Account" data-testid="doctor-social-input" value={form.fb_insta} onChange={(e) => upd("fb_insta", e.target.value)} placeholder="@handle" />
          <Field label="YouTube Channel" data-testid="doctor-yt-input" value={form.yt_channel} onChange={(e) => upd("yt_channel", e.target.value)} placeholder="youtube.com/@channel" />
          <Field label="CC Number" data-testid="doctor-cc-input" value={form.cc_number} onChange={(e) => upd("cc_number", e.target.value)} placeholder="CC code" />
          <Field label="Place" data-testid="doctor-place-input" value={form.place} onChange={(e) => upd("place", e.target.value)} placeholder="Mumbai" />
        </div>
      )}

      {step === 1 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="font-semibold mb-2">📋 Recording guidelines</p>
          <div className="flex gap-4 items-start">
            <div className="flex-shrink-0 w-40 rounded-md overflow-hidden border border-amber-300">
              <img src="/example-image.jpeg" alt="Reference – doctor centred on screen" className="w-full h-auto" />
            </div>
            <ul className="list-disc list-inside space-y-1 text-amber-700 pt-1">
              <li>Please refer the photo for position of Doctor on Mobile screen</li>
              <li>Doctor's face should be in the <strong>centre of the screen</strong></li>
              <li>If possible, please use the <strong>rear (main) camera</strong> instead of the selfie camera</li>
              <li>Hold the phone in <strong>portrait</strong> orientation</li>
            </ul>
          </div>
        </div>
      )}

      {/* STEP 2 — template selection */}
      {step === 2 && (
        <div className="animate-fade-up space-y-5" data-testid="step-template">
          {/* SOP / instructions */}
          <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="template-sop">
            <Info className="h-5 w-5 shrink-0 mt-0.5 text-emerald-600" />
            <div>
              <p className="font-semibold">Before you choose — watch the template</p>
              <ol className="mt-1.5 list-decimal space-y-0.5 pl-4 text-emerald-800/90">
                <li>Tap <span className="font-medium">Play</span> on a template to preview the full greeting video with audio.</li>
                <li>Watch it end-to-end so you know exactly what the doctor's video will look like.</li>
                <li>Pick the template the doctor wants, then tap <span className="font-medium">Continue</span> to add their video.</li>
              </ol>
            </div>
          </div>

          {loadingTpl ? (
            <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-emerald-600" /></div>
          ) : templates.length === 0 ? (
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="no-template-warning">
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
              <p>No templates available yet. Please ask your admin to add one before generating videos.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {templates.map((t) => (
                <div key={t.id}
                  data-testid={`template-option-${t.id}`}
                  className={`group rounded-lg border-2 overflow-hidden transition-all ${templateId === t.id ? "border-emerald-500 ring-2 ring-emerald-200" : "border-stone-200 hover:border-emerald-300"}`}>
                  <div className="relative aspect-video bg-stone-900">
                    <video
                      ref={(el) => { videoRefs.current[t.id] = el; }}
                      src={t.playback_url || `${API}/templates/${t.id}/preview`}
                      playsInline
                      preload="metadata"
                      onEnded={() => setPlayingId((id) => (id === t.id ? null : id))}
                      controls={playingId === t.id}
                      className="h-full w-full object-cover"
                    />
                    {playingId !== t.id && (
                      <button type="button" onClick={() => togglePlay(t.id)}
                        data-testid={`template-play-${t.id}`}
                        className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors hover:bg-black/40">
                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-emerald-700 shadow-lg">
                          <Play className="h-5 w-5 translate-x-0.5" fill="currentColor" />
                        </span>
                      </button>
                    )}
                    {templateId === t.id && (
                      <div className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-600 text-white"><Check className="h-4 w-4" /></div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="font-medium text-stone-900 text-sm truncate">{t.asana_name}</p>
                    {t.duration ? <p className="text-xs text-stone-400">{Math.round(t.duration)}s</p> : null}
                    <button type="button" onClick={() => setTemplateId(t.id)}
                      data-testid={`template-select-${t.id}`}
                      className={`mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${templateId === t.id ? "bg-emerald-600 text-white" : "border border-stone-200 bg-white text-stone-700 hover:bg-stone-50"}`}>
                      {templateId === t.id ? (<><Check className="h-3.5 w-3.5" /> Selected</>) : "Select this template"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* STEP 3 — upload or record */}
      {step === 3 && (
        <div className="space-y-5 animate-fade-up" data-testid="step-video">
          <div
            onClick={() => !uploading && inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
            data-testid="upload-video-dropzone"
            className={`relative flex flex-col items-center justify-center min-h-[220px] rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-all ${drag ? "border-emerald-500 bg-emerald-50" : "border-stone-300 bg-stone-50 hover:bg-stone-100 hover:border-emerald-400"}`}
          >
            <input ref={inputRef} type="file" accept=".mp4,.mov,.webm,video/mp4,video/quicktime,video/webm" className="hidden"
              data-testid="doctor-video-input" onChange={(e) => pick(e.target.files?.[0])} />
            {file ? (
              <div className="flex flex-col items-center gap-2">
                <Video className="h-9 w-9 text-emerald-600" />
                <p className="font-medium text-stone-800" data-testid="selected-file-name">{file.name}</p>
                <p className="text-xs text-stone-400">{(file.size / 1024 / 1024).toFixed(1)} MB</p>
                <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="mt-1 inline-flex items-center gap-1 text-xs text-stone-500 hover:text-red-600"><X className="h-3 w-3" /> Remove</button>
              </div>
            ) : (
              <>
                <UploadCloud className="h-9 w-9 text-emerald-600" />
                <p className="mt-3 font-medium text-stone-700">Drag & drop or click to select</p>
                <p className="text-sm text-stone-400">Portrait MP4 / MOV / WEBM · max 200MB</p>
              </>
            )}
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <p className="font-semibold mb-2">📋 Recording guidelines</p>
            <div className="flex gap-4 items-start">
              <div className="flex-shrink-0 w-40 rounded-md overflow-hidden border border-amber-300">
                <img src="/example-image.jpeg" alt="Reference – doctor centred on screen" className="w-full h-auto" />
              </div>
              <ul className="list-disc list-inside space-y-1 text-amber-700 pt-1">
                <li>Please refer the photo for position of Doctor on Mobile screen</li>
                <li>Doctor's face should be in the <strong>centre of the screen</strong></li>
                <li>If possible, please use the <strong>rear (main) camera</strong> instead of the selfie camera</li>
                <li>Hold the phone in <strong>portrait</strong> orientation</li>
              </ul>
            </div>
          </div>

          {uploading && (
            <div data-testid="upload-progress">
              <div className="flex justify-between text-sm text-stone-500 mb-1"><span>Uploading…</span><span>{progress}%</span></div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-stone-200">
                <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* nav buttons */}
      <div className="flex items-center justify-between pt-2">
        <button type="button" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1 || uploading}
          data-testid="wizard-back"
          className="inline-flex items-center gap-1.5 rounded-md border border-stone-200 bg-white px-5 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-40">
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        {step < 3 ? (
          <button type="button" onClick={next} data-testid="wizard-next"
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
            Continue <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button type="button" onClick={submit} disabled={uploading || !file} data-testid="submit-upload-button"
            className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Film className="h-4 w-4" />} Generate video
          </button>
        )}
      </div>
    </div>
  );
}
